import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import {
  Megaphone, Loader2, RefreshCw, Play, Pause, Plus,
  TrendingUp, MousePointerClick, Eye, DollarSign, Target,
  AlertCircle, CheckCircle2
} from 'lucide-react';
import { useCan, Can } from '../../lib/rbac';

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
const fmtN = (n: number) => new Intl.NumberFormat('en-US').format(n);
const fmtPct = (n: number) => `${n.toFixed(1)}%`;

interface Campaign {
  id: number;
  site_id: string;
  ml_campaign_id: string;
  name: string;
  status: string;
  daily_budget_usd: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  total_spent: number;
  acos: number;
  last_sync_at: string;
}

interface Totals {
  total_campaigns: number;
  active_campaigns: number;
  total_impressions: number;
  total_clicks: number;
  total_spent: number;
  total_revenue: number;
  overall_acos: number;
}

interface EligibilityData {
  eligible: boolean;
  advertisers: Array<{ advertiser_id: number; site_id: string; advertiser_name: string }>;
}

export default function AdsTab({ sellerId }: { sellerId: number }) {
  const can = useCan();
  const canMoney = can('global.view_money');
  // Sin la llave del dinero los importes se muestran como "—".
  const money = (n: number) => (canMoney ? fmt(n) : '\u2014');
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [eligibility, setEligibility] = useState<EligibilityData | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ campaigns: Campaign[]; totals: Totals }>(`/mercadolibre/ads/summary?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setCampaigns((res.data as any).campaigns || []);
        setTotals((res.data as any).totals || null);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [sellerId]);

  useEffect(() => { loadData(); }, [loadData]);

  const checkEligibility = async () => {
    setCheckingEligibility(true);
    try {
      const res = await api.get<EligibilityData>(`/mercadolibre/ads/eligibility?seller_id=${sellerId}`);
      if (res.success && res.data) setEligibility(res.data as any);
    } catch { /* ignore */ }
    setCheckingEligibility(false);
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post(`/scheduler/product_ads/run?seller_id=${sellerId}`, {});
      setTimeout(loadData, 5000);
    } catch { /* ignore */ }
    setSyncing(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  const ctr = totals && totals.total_impressions > 0 ? (totals.total_clicks / totals.total_impressions * 100) : 0;
  const roas = totals && totals.total_spent > 0 ? (totals.total_revenue / totals.total_spent) : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-green-500" />
          <h2 className="text-lg font-bold text-[var(--color-text)]">Product Ads (PPC)</h2>
        </div>
        <div className="flex gap-2">
          <Can cap="ml.ads_sync">
          <button onClick={checkEligibility} disabled={checkingEligibility}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-bg-tertiary)]">
            {checkingEligibility ? <Loader2 className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3" />}
            Check Eligibility
          </button>
          <button onClick={syncNow} disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-bg-tertiary)]">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Sync Metrics
          </button>
          </Can>
        </div>
      </div>

      {/* Info banner */}
      <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-xs text-[var(--color-text-tertiary)] leading-relaxed">
        <p className="font-semibold text-[var(--color-text-secondary)] mb-1">Que es Product Ads?</p>
        <p>Publicidad pagada (PPC) en MercadoLibre. Tus productos aparecen en posiciones destacadas en los resultados de busqueda. Pagas por cada click que reciben.</p>
        <p className="mt-1"><strong>ROAS</strong> = Revenue / Ad Spend (cuanto ganas por cada dolar invertido). Objetivo: &gt;5x.</p>
        <p><strong>ACOS</strong> = Ad Spend / Revenue (que porcentaje de tus ventas se va en publicidad). Objetivo: &lt;20%.</p>
        <p className="mt-1">Para empezar: 1) Activa Product Ads desde el panel de ML. 2) Click "Check Eligibility" abajo. 3) Configura el proceso en AI → Processes → Product Ads.</p>
      </div>

      {/* Eligibility banner */}
      {eligibility && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${
          eligibility.eligible
            ? 'bg-green-500/5 border-green-500/20 text-green-400'
            : 'bg-red-500/5 border-red-500/20 text-red-400'
        }`}>
          {eligibility.eligible ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span className="text-sm">
            {eligibility.eligible
              ? `Eligible — ${eligibility.advertisers.length} advertiser(s): ${eligibility.advertisers.map(a => `${a.site_id} (#${a.advertiser_id})`).join(', ')}`
              : 'Not eligible for Product Ads. Activate from ML seller panel first.'}
          </span>
        </div>
      )}

      {/* KPI Cards */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KPICard icon={<Megaphone className="w-4 h-4" />} label="Campaigns" value={`${totals.active_campaigns} / ${totals.total_campaigns}`} color="green" />
          <KPICard icon={<Eye className="w-4 h-4" />} label="Impressions" value={fmtN(totals.total_impressions)} color="blue" />
          <KPICard icon={<MousePointerClick className="w-4 h-4" />} label="Clicks" value={fmtN(totals.total_clicks)} sub={`CTR ${fmtPct(ctr)}`} color="cyan" />
          <KPICard icon={<DollarSign className="w-4 h-4" />} label="Spent" value={money(totals.total_spent)} color="yellow" />
          <KPICard icon={<TrendingUp className="w-4 h-4" />} label="Revenue" value={money(totals.total_revenue)} color="green" />
          {canMoney && <KPICard icon={<Target className="w-4 h-4" />} label="ROAS" value={`${roas.toFixed(1)}x`} sub={`ACOS ${fmtPct(totals.overall_acos)}`} color={roas >= 5 ? 'green' : roas >= 2 ? 'yellow' : 'red'} />}
        </div>
      )}

      {/* Campaigns Table */}
      {campaigns.length > 0 ? (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-[var(--color-text-tertiary)]">
                <th className="text-left px-4 py-2 font-medium">Campaign</th>
                <th className="text-left px-3 py-2 font-medium">Site</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Budget/day</th>
                <th className="text-right px-3 py-2 font-medium">Impressions</th>
                <th className="text-right px-3 py-2 font-medium">Clicks</th>
                <th className="text-right px-3 py-2 font-medium">Spent</th>
                <th className="text-right px-3 py-2 font-medium">Revenue</th>
                <th className="text-right px-3 py-2 font-medium">ACOS</th>
                <th className="text-right px-3 py-2 font-medium">ROAS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {campaigns.map((c) => {
                const campaignRoas = c.total_spent > 0 ? c.revenue / c.total_spent : 0;
                return (
                  <tr key={c.id} className="hover:bg-[var(--color-bg-tertiary)]">
                    <td className="px-4 py-2 font-medium text-[var(--color-text)]">{c.name || c.ml_campaign_id}</td>
                    <td className="px-3 py-2 text-[var(--color-text-secondary)]">{c.site_id}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                        c.status === 'active' ? 'bg-green-500/10 text-green-400' :
                        c.status === 'paused' ? 'bg-yellow-500/10 text-yellow-400' :
                        'bg-gray-500/10 text-gray-400'
                      }`}>
                        {c.status === 'active' ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--color-text)]">{money(c.daily_budget_usd)}</td>
                    <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{fmtN(c.impressions)}</td>
                    <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{fmtN(c.clicks)}</td>
                    <td className="px-3 py-2 text-right text-yellow-400">{money(c.total_spent)}</td>
                    <td className="px-3 py-2 text-right text-green-400">{money(c.revenue)}</td>
                    <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{canMoney ? fmtPct(c.acos) : '\u2014'}</td>
                    <td className={`px-3 py-2 text-right font-medium ${
                      campaignRoas >= 5 ? 'text-green-400' : campaignRoas >= 2 ? 'text-yellow-400' : 'text-red-400'
                    }`}>{canMoney ? `${campaignRoas.toFixed(1)}x` : '\u2014'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-[var(--color-text-tertiary)]">
          <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No campaigns synced yet.</p>
          <p className="text-xs mt-1">Enable the Product Ads process and click "Sync Metrics" to get started.</p>
        </div>
      )}
    </div>
  );
}

function KPICard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-${color}-500`}>{icon}</span>
        <span className="text-xs text-[var(--color-text-tertiary)]">{label}</span>
      </div>
      <p className="text-lg font-bold text-[var(--color-text)]">{value}</p>
      {sub && <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">{sub}</p>}
    </div>
  );
}
